/* @jsx h */
import {
  Teleport,
  computed,
  defineComponent,
  h,
  inject,
  nextTick,
  onBeforeUnmount,
  provide,
  ref,
  watch,
  type Ref,
} from "vue";
import {
  articleVisibilityOf,
  articleVisibilityOptions,
  canViewArticle,
  environmentVisibilityOptions,
  getTrainingTutorial,
  isTutorialInAppEnvironment,
  localizeText,
  majorVersion,
  parseRichText,
  resolveEnvironmentVisibility,
  type AppEnvironment,
  type ArticleVisibility,
  type EnvironmentVisibility,
  type RichTextBlock,
  type RichTextInline,
  type RichTextVideoBlock,
  type TrainingTutorial,
} from "@lemmonhq/knowledgebase";
import { LemmonModal, supportedLanguages, useLocalization } from "@lemmonhq/uikit";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "@app/vue/providers/Auth";
import { evaluateCondition } from "./conditions";
import type { TrainingContext } from "./predicates";
import { installTriggerController } from "./TriggerController";
import {
  PADDING,
  TOOLTIP_WIDTH,
  VIEWPORT_MARGIN,
  computeTooltipPos,
  pickPlacement,
  type Rect,
} from "./placement";
import {
  activeTutorialDataId,
  isTutorialDataActive,
  requiredTutorialOverlay,
  tutorialLocale,
  tutorialStepTick,
} from "@app/data/tutorialData";

type TrainingContextType = {
  activeTutorial: Ref<TrainingTutorial | null>;
  activeStepIndex: Ref<number>;
  start: (tutorialId: string, options?: { force?: boolean; stepIndex?: number }) => void;
  pause: () => void;
  skip: () => void;
  finish: () => void;
  isCompleted: (tutorialId: string) => boolean;
};

const TrainingContextKey = Symbol("TrainingContext");

// The app's runtime environment for tutorial visibility. Staging/local count as
// "development"; only an explicit production build is "production".
const currentAppEnvironment: AppEnvironment =
  import.meta.env.VITE_ENV === "production" ? "production" : "development";

// apps/web only ever serves authenticated app users (admin/manager/staff), which map to
// the "guest" knowledge-base role for article (role) visibility. The KB "admin" tier
// lives in the support center, not here.
const APP_KB_ROLE = "guest" as const;

// A tutorial is available in the app when BOTH gates pass: its role visibility admits a
// guest, and its environment visibility includes the current environment.
const isTutorialAvailableInApp = (tutorial: TrainingTutorial): boolean =>
  canViewArticle(tutorial.visibility, APP_KB_ROLE) &&
  isTutorialInAppEnvironment(tutorial, currentAppEnvironment);

// Human-readable labels for the dev editor's visibility dropdowns.
const roleVisibilityLabels: Record<ArticleVisibility, string> = {
  admin: "Admin only",
  guest: "Guests only",
  everyone: "Everyone",
};
const environmentVisibilityLabels: Record<EnvironmentVisibility, string> = {
  production: "Production only",
  development: "Development only",
  both: "Production & development",
  nowhere: "Nowhere in the app",
};

const chromeText = {
  en: {
    step: "Step {0} of {1}",
    next: "Next",
    back: "Back",
    pause: "Pause",
    skip: "Skip",
    finish: "Finish",
  },
  de: {
    step: "Schritt {0} von {1}",
    next: "Weiter",
    back: "Zurück",
    pause: "Pausieren",
    skip: "Überspringen",
    finish: "Fertig",
  },
  fr: {
    step: "Étape {0} sur {1}",
    next: "Suivant",
    back: "Retour",
    pause: "Pause",
    skip: "Ignorer",
    finish: "Terminer",
  },
  it: {
    step: "Passaggio {0} di {1}",
    next: "Avanti",
    back: "Indietro",
    pause: "Pausa",
    skip: "Salta",
    finish: "Fine",
  },
  ka: {
    step: "ნაბიჯი {0}/{1}",
    next: "შემდეგი",
    back: "უკან",
    pause: "პაუზა",
    skip: "გამოტოვება",
    finish: "დასრულება",
  },
} as const;

const localeKey = (locale: string) => (locale.split("-")[0].split("_")[0] || "en") as keyof typeof chromeText;

// Completion/paused state is keyed on the MAJOR version only, so patch/minor edits
// from the tutorial editor don't reset progress for users who already finished it.
const storageKey = (tutorial: TrainingTutorial) =>
  `training.${tutorial.id}.v${majorVersion(tutorial.version)}.completed`;

const pausedStepKey = (tutorial: TrainingTutorial) =>
  `training.${tutorial.id}.v${majorVersion(tutorial.version)}.paused_step`;

const safeLocalStorageGet = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore unavailable localStorage */
  }
};

const safeLocalStorageRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore unavailable localStorage */
  }
};

const getRect = (selector?: string): Rect | null => {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const renderInline = (node: RichTextInline, index: number) => {
  if (node.type === "strong") {
    return <strong key={index}>{node.text}</strong>;
  }
  if (node.type === "code") {
    return (
      <code key={index} class="rounded bg-base-300 px-1 py-0.5 text-[0.9em]">
        {node.text}
      </code>
    );
  }
  if (node.type === "link") {
    return (
      <a key={index} href={node.href} target="_blank" rel="noreferrer" class="link link-primary">
        {node.text}
      </a>
    );
  }
  return node.text;
};

export const TrainingProvider = defineComponent({
  name: "TrainingProvider",
  setup(_, { slots }) {
    const localization = useLocalization();
    const router = useRouter();
    const route = useRoute();
    const auth = useAuth();
    const activeTutorial = ref<TrainingTutorial | null>(null);
    const activeStepIndex = ref(0);
    const targetRect = ref<Rect | null>(null);
    // Optional finer anchor for the idle-hint dot: lets the spotlight frame a whole
    // control group (`target`) while the dot focuses on one sub-element (`hintTarget`).
    const hintRect = ref<Rect | null>(null);
    const tooltipHeight = ref(160);
    const viewport = ref({ w: window.innerWidth, h: window.innerHeight });
    const tooltipRef = ref<HTMLElement | null>(null);
    const activeVideo = ref<RichTextVideoBlock | null>(null);
    const showIdleHint = ref(false);
    let idleHintTimer: number | null = null;
    let hintHideTimer: number | null = null;

    // Manual override for the tooltip position once the user drags the card by its
    // move handle. `null` means "follow the computed placement for this step"; it is
    // reset on every step change / tutorial start (see syncPosition) so each step
    // re-anchors to its target before the user nudges it.
    const manualTooltipPos = ref<{ top: number; left: number } | null>(null);
    let tooltipDrag: { startX: number; startY: number; baseTop: number; baseLeft: number } | null = null;

    const isDev = import.meta.env.DEV;
    const editMode = ref(false);
    const editDrafts = ref<{
      tutorialTitle: string;
      tutorialSummary: string;
      stepTitle: string;
      stepBody: string;
      visibility: ArticleVisibility;
      environmentVisibility: EnvironmentVisibility;
    } | null>(null);
    const autoTranslate = ref(true);
    const saving = ref(false);
    const saveError = ref<string | null>(null);
    const historyEntries = ref<{ index: number; version: string; savedAt: string }[]>([]);
    const historyLoading = ref(false);
    const historyError = ref<string | null>(null);
    const restoring = ref<number | null>(null);
    const showHistory = ref(false);

    const enterEditMode = () => {
      const t = activeTutorial.value;
      const s = activeStep.value;
      if (!t || !s) return;
      const lk = locale.value;
      editDrafts.value = {
        tutorialTitle: localizeText(t.title, lk),
        tutorialSummary: localizeText(t.summary, lk),
        stepTitle: localizeText(s.title, lk),
        stepBody: localizeText(s.body, lk),
        visibility: articleVisibilityOf(t.visibility),
        environmentVisibility: resolveEnvironmentVisibility(t),
      };
      saveError.value = null;
      showHistory.value = false;
      historyError.value = null;
      editMode.value = true;
      void loadHistory();
    };

    const cancelEdit = () => {
      editMode.value = false;
      editDrafts.value = null;
      saveError.value = null;
      showHistory.value = false;
    };

    type EditTarget =
      | { kind: "tutorial"; field: "title" | "summary" }
      | {
          kind: "tutorial-meta";
          field: "version" | "visibility" | "environmentVisibility";
        }
      | { kind: "step"; stepId: string; field: "title" | "body" };

    type SaveEdit = { target: EditTarget; locale: string; value: string };
    type HistoryEntry = { index: number; version: string; savedAt: string };

    const loadHistory = async () => {
      const t = activeTutorial.value;
      if (!t) return;
      historyLoading.value = true;
      historyError.value = null;
      try {
        const r = await fetch(`/__tutorial-history?tutorialId=${encodeURIComponent(t.id)}`);
        const data = (await r.json()) as {
          ok: boolean;
          entries?: HistoryEntry[];
          error?: string;
        };
        if (!data.ok) throw new Error(data.error || "Failed to load history");
        historyEntries.value = data.entries || [];
      } catch (err) {
        historyError.value = err instanceof Error ? err.message : "Unknown error";
      } finally {
        historyLoading.value = false;
      }
    };

    const restoreVersion = async (index: number) => {
      const t = activeTutorial.value;
      if (!t) return;
      restoring.value = index;
      historyError.value = null;
      try {
        const r = await fetch("/__tutorial-restore", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ tutorialId: t.id, index }),
        });
        const data = (await r.json()) as { ok: boolean; version?: string; error?: string };
        if (!data.ok) throw new Error(data.error || "Restore failed");
        // The source YAML changed on disk — reload so the imported tutorial reflects it.
        window.location.reload();
      } catch (err) {
        historyError.value = err instanceof Error ? err.message : "Unknown error";
        restoring.value = null;
      }
    };

    const translateToOthers = async (
      text: string,
      sourceLang: string,
      targetLangs: string[],
    ): Promise<Record<string, string>> => {
      if (targetLangs.length === 0) return {};
      const r = await fetch("/__tutorial-translate", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text, sourceLang, targetLangs }),
      });
      const data = (await r.json()) as {
        ok: boolean;
        results?: Record<string, string>;
        error?: string;
      };
      if (!data.ok) throw new Error(data.error || "Translate failed");
      return data.results || {};
    };

    const saveEdits = async () => {
      const t = activeTutorial.value;
      const s = activeStep.value;
      const drafts = editDrafts.value;
      if (!t || !s || !drafts) return;
      const lk = locale.value;
      saving.value = true;
      saveError.value = null;
      try {
        const original = {
          tutorialTitle: localizeText(t.title, lk),
          tutorialSummary: localizeText(t.summary, lk),
          stepTitle: localizeText(s.title, lk),
          stepBody: localizeText(s.body, lk),
        };
        type Job = {
          target: EditTarget;
          value: string;
          apply: (lang: string, value: string) => void;
        };
        const jobs: Job[] = [];
        if (drafts.tutorialTitle !== original.tutorialTitle) {
          jobs.push({
            target: { kind: "tutorial", field: "title" },
            value: drafts.tutorialTitle,
            apply: (lang, value) => {
              (t.title as Record<string, string>)[lang] = value;
            },
          });
        }
        if (drafts.tutorialSummary !== original.tutorialSummary) {
          jobs.push({
            target: { kind: "tutorial", field: "summary" },
            value: drafts.tutorialSummary,
            apply: (lang, value) => {
              (t.summary as Record<string, string>)[lang] = value;
            },
          });
        }
        if (drafts.stepTitle !== original.stepTitle) {
          jobs.push({
            target: { kind: "step", stepId: s.id, field: "title" },
            value: drafts.stepTitle,
            apply: (lang, value) => {
              (s.title as Record<string, string>)[lang] = value;
            },
          });
        }
        if (drafts.stepBody !== original.stepBody) {
          jobs.push({
            target: { kind: "step", stepId: s.id, field: "body" },
            value: drafts.stepBody,
            apply: (lang, value) => {
              (s.body as Record<string, string>)[lang] = value;
            },
          });
        }

        // Visibility settings are tutorial metadata: a single scalar each, not
        // localized and never auto-translated. Collect any changes alongside the jobs.
        const metaEdits: SaveEdit[] = [];
        const metaAppliers: Array<() => void> = [];
        if (drafts.visibility !== articleVisibilityOf(t.visibility)) {
          metaEdits.push({
            target: { kind: "tutorial-meta", field: "visibility" },
            locale: "",
            value: drafts.visibility,
          });
          metaAppliers.push(() => {
            t.visibility = drafts.visibility;
          });
        }
        if (drafts.environmentVisibility !== resolveEnvironmentVisibility(t)) {
          metaEdits.push({
            target: { kind: "tutorial-meta", field: "environmentVisibility" },
            locale: "",
            value: drafts.environmentVisibility,
          });
          metaAppliers.push(() => {
            t.environmentVisibility = drafts.environmentVisibility;
          });
        }

        if (jobs.length === 0 && metaEdits.length === 0) {
          editMode.value = false;
          editDrafts.value = null;
          return;
        }

        // Gather every locale change (source + optional translations) up front, then
        // commit them in one atomic save that snapshots history and bumps the patch.
        const edits: SaveEdit[] = [];
        const appliers: Array<() => void> = [];
        const others = supportedLanguages.map((l) => l.id).filter((id) => id !== lk);
        for (const job of jobs) {
          edits.push({ target: job.target, locale: lk, value: job.value });
          appliers.push(() => job.apply(lk, job.value));
          if (autoTranslate.value && others.length > 0) {
            const results = await translateToOthers(job.value, lk, others);
            for (const [lang, value] of Object.entries(results)) {
              if (!value) continue;
              edits.push({ target: job.target, locale: lang, value });
              appliers.push(() => job.apply(lang, value));
            }
          }
        }
        edits.push(...metaEdits);
        appliers.push(...metaAppliers);

        const r = await fetch("/__tutorial-save", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ tutorialId: t.id, edits }),
        });
        const data = (await r.json()) as { ok: boolean; version?: string; error?: string };
        if (!data.ok) throw new Error(data.error || "Save failed");

        for (const apply of appliers) apply();
        if (data.version) t.version = data.version;

        editMode.value = false;
        editDrafts.value = null;
      } catch (err) {
        saveError.value = err instanceof Error ? err.message : "Unknown error";
      } finally {
        saving.value = false;
      }
    };

    const activeStep = computed(() => activeTutorial.value?.steps[activeStepIndex.value] || null);

    const locale = computed(() => localeKey(localization.locale.value));
    const copy = computed(() => chromeText[locale.value] || chromeText.en);

    const refreshRect = () => {
      viewport.value = { w: window.innerWidth, h: window.innerHeight };
      targetRect.value = getRect(activeStep.value?.target);
      hintRect.value = getRect(activeStep.value?.hintTarget);
      if (tooltipRef.value) {
        tooltipHeight.value = tooltipRef.value.offsetHeight || 160;
      }
    };

    const scrollTargetIntoView = () => {
      const selector = activeStep.value?.target;
      if (!selector) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      el?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    };

    // Idle-hint timing:
    // - The red dot appears after IDLE_HINT_DELAY of inactivity on a step.
    // - When the user moves the mouse while the dot is showing, we don't hide it
    //   instantly: we let it blink one more time (HINT_LINGER) before hiding, then
    //   re-arm the idle countdown so it reappears once the user settles again.
    const IDLE_HINT_DELAY = 2000;
    const HINT_LINGER = 1200;

    const scheduleIdleShow = () => {
      if (idleHintTimer !== null) window.clearTimeout(idleHintTimer);
      idleHintTimer = window.setTimeout(() => {
        idleHintTimer = null;
        if (!activeTutorial.value) return;
        if (hintHideTimer !== null) {
          window.clearTimeout(hintHideTimer);
          hintHideTimer = null;
        }
        showIdleHint.value = true;
      }, IDLE_HINT_DELAY);
    };

    const clearIdleHint = () => {
      if (idleHintTimer !== null) {
        window.clearTimeout(idleHintTimer);
        idleHintTimer = null;
      }
      if (hintHideTimer !== null) {
        window.clearTimeout(hintHideTimer);
        hintHideTimer = null;
      }
      showIdleHint.value = false;
    };

    const armIdleHint = () => {
      clearIdleHint();
      if (!activeTutorial.value) return;
      scheduleIdleShow();
    };

    const syncTimers = new Set<number>();
    const clearSyncTimers = () => {
      for (const id of syncTimers) window.clearTimeout(id);
      syncTimers.clear();
    };
    const scheduleSync = (fn: () => void, delay: number) => {
      const id = window.setTimeout(() => {
        syncTimers.delete(id);
        if (!activeTutorial.value) return;
        try {
          fn();
        } catch (err) {
          console.warn("Training syncPosition step failed", err);
        }
      }, delay);
      syncTimers.add(id);
    };

    const syncPosition = async () => {
      clearSyncTimers();
      // A new step re-anchors to its own target: drop any manual drag offset.
      manualTooltipPos.value = null;
      await nextTick();
      if (!activeTutorial.value) return;
      try {
        scrollTargetIntoView();
      } catch (err) {
        console.warn("Training scrollTargetIntoView failed", err);
      }
      scheduleSync(refreshRect, 50);
      scheduleSync(refreshRect, 250);
      scheduleSync(refreshRect, 600);
      scheduleSync(() => {
        scrollTargetIntoView();
        refreshRect();
      }, 1200);
      armIdleHint();
    };

    const isCompleted = (tutorialId: string) => {
      const tutorial = getTrainingTutorial(tutorialId);
      if (!tutorial) return false;
      if (safeLocalStorageGet(storageKey(tutorial)) === "1") return true;
      if (tutorial.completion !== undefined) return evaluate(tutorial.completion);
      return false;
    };

    const start: TrainingContextType["start"] = (tutorialId, options = {}) => {
      const { force = false, stepIndex } = options;
      const tutorial = getTrainingTutorial(tutorialId);
      if (!tutorial) return;
      // Visibility gate: never start a tutorial the current environment/role may not
      // see — this also blocks shared deep-links (?tutorial=) to admin-only, hidden, or
      // wrong-environment tutorials. `force` only bypasses completion, not visibility.
      if (!isTutorialAvailableInApp(tutorial)) return;
      if (!force && isCompleted(tutorial.id)) return;

      const paused = Number(safeLocalStorageGet(pausedStepKey(tutorial)));
      const resolvedStepIndex = typeof stepIndex === "number" ? stepIndex : Number.isFinite(paused) ? paused : 0;

      // Next-only progression: never auto-skip a step at start. The user always
      // advances deliberately via the Next button.
      const initialIndex = Math.min(Math.max(resolvedStepIndex, 0), tutorial.steps.length - 1);

      activeTutorial.value = tutorial;
      activeStepIndex.value = initialIndex;
      syncPosition();
    };

    const cleanupAfterExit = () => {
      activeVideo.value = null;
      editMode.value = false;
      editDrafts.value = null;
      saveError.value = null;
      saving.value = false;
      showHistory.value = false;
      activeTutorial.value = null;
      activeStepIndex.value = 0;
      requiredTutorialOverlay.value = null;
      // Overlay is no longer required: bump the tick so owners close the sheet/menu we
      // deliberately kept open on the end card (see the last-step branch above).
      tutorialStepTick.value += 1;
      manualTooltipPos.value = null;
      endTooltipDrag();
      clearIdleHint();
      clearSyncTimers();
    };

    const pause = () => {
      try {
        const tutorial = activeTutorial.value;
        if (tutorial) {
          safeLocalStorageSet(pausedStepKey(tutorial), String(activeStepIndex.value));
        }
      } catch (err) {
        console.warn("Training pause failed", err);
      } finally {
        cleanupAfterExit();
      }
    };

    const skip = () => {
      try {
        const tutorial = activeTutorial.value;
        if (tutorial) {
          safeLocalStorageSet(storageKey(tutorial), "1");
          safeLocalStorageRemove(pausedStepKey(tutorial));
        }
      } catch (err) {
        console.warn("Training skip failed", err);
      } finally {
        cleanupAfterExit();
      }
    };

    const finish = () => {
      try {
        const tutorial = activeTutorial.value;
        if (tutorial) {
          safeLocalStorageSet(storageKey(tutorial), "1");
          safeLocalStorageRemove(pausedStepKey(tutorial));
        }
      } catch (err) {
        console.warn("Training finish failed", err);
      } finally {
        cleanupAfterExit();
      }
    };

    const startNext = () => {
      const tutorial = activeTutorial.value;
      const nextId = tutorial?.next;
      if (!nextId) {
        finish();
        return;
      }
      if (tutorial) {
        safeLocalStorageSet(storageKey(tutorial), "1");
        safeLocalStorageRemove(pausedStepKey(tutorial));
      }
      start(nextId, { force: true });
    };

    const goNext = (opts: { manual?: boolean } = {}) => {
      const tutorial = activeTutorial.value;
      if (!tutorial) return;
      const step = activeStep.value;
      // Next-only progression: the only gate is the step's validation (rare). When it
      // isn't satisfied yet, surface the hint instead of advancing.
      if (opts.manual && !stepValidationOk(step)) {
        showIdleHint.value = true;
        return;
      }
      if (activeStepIndex.value < tutorial.steps.length - 1) {
        activeStepIndex.value += 1;
      } else {
        finish();
      }
    };

    const goBack = () => {
      if (activeStepIndex.value > 0) {
        activeStepIndex.value -= 1;
      }
    };

    const jumpToStep = (index: number) => {
      const tutorial = activeTutorial.value;
      if (!tutorial) return;
      activeStepIndex.value = Math.min(Math.max(index, 0), tutorial.steps.length - 1);
    };

    const guard =
      <T extends unknown[]>(label: string, fn: (...args: T) => void) =>
      (...args: T) => {
        try {
          fn(...args);
        } catch (err) {
          console.warn(`Training ${label} failed`, err);
        }
      };

    const isEditableTarget = (el: EventTarget | null) => {
      const node = el as HTMLElement | null;
      if (!node || typeof node.tagName !== "string") return false;
      const tag = node.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || node.isContentEditable === true;
    };

    const onKey = guard("onKey", (event: KeyboardEvent) => {
      if (!activeTutorial.value) return;
      // While editing the tutorial, the panel owns the keyboard (typing in inputs/textarea).
      if (editMode.value) return;
      if (event.key === "Escape") {
        event.preventDefault();
        skip();
      } else if (event.key === "ArrowRight" || event.key === "Enter") {
        // Deliberate keyboard equivalent of clicking Next (same validation gate). Don't
        // hijack Enter/→ while the user is typing in or submitting an app field.
        if (isEditableTarget(event.target)) return;
        event.preventDefault();
        goNext({ manual: true });
      } else if (event.key === "ArrowLeft") {
        if (isEditableTarget(event.target)) return;
        event.preventDefault();
        goBack();
      }
    });

    const onWindowChange = guard("onWindowChange", () => {
      if (!activeTutorial.value) return;
      refreshRect();
    });

    // Next-only progression: clicks on the spotlighted target and value changes no
    // longer advance the tutorial. The user always moves forward via the Next button
    // (or its Enter / → keyboard equivalents). The target stays interactive so the
    // user can perform the real action; it just doesn't trigger advancement.

    const onUserActivity = guard("onUserActivity", () => {
      if (!activeTutorial.value) return;
      // If the dot is currently visible, let it blink one more time before hiding
      // rather than vanishing the instant the mouse twitches. A single pending
      // hide timer coalesces rapid movement so there's no flicker.
      if (showIdleHint.value && hintHideTimer === null) {
        hintHideTimer = window.setTimeout(() => {
          hintHideTimer = null;
          showIdleHint.value = false;
        }, HINT_LINGER);
      }
      // Re-arm the idle countdown so the dot reappears once the user settles.
      scheduleIdleShow();
    });

    // Dragging the card by its move handle. We seed the drag from the card's live
    // on-screen rect, so it doesn't matter whether the current position came from the
    // computed placement or a previous manual drag, and clamp it to the viewport.
    const onTooltipDragMove = guard("onTooltipDragMove", (event: PointerEvent) => {
      if (!tooltipDrag) return;
      const w = tooltipRef.value?.offsetWidth ?? TOOLTIP_WIDTH;
      const hgt = tooltipRef.value?.offsetHeight ?? tooltipHeight.value;
      const maxTop = Math.max(VIEWPORT_MARGIN, viewport.value.h - hgt - VIEWPORT_MARGIN);
      const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.value.w - w - VIEWPORT_MARGIN);
      const nextTop = tooltipDrag.baseTop + (event.clientY - tooltipDrag.startY);
      const nextLeft = tooltipDrag.baseLeft + (event.clientX - tooltipDrag.startX);
      manualTooltipPos.value = {
        top: Math.min(Math.max(VIEWPORT_MARGIN, nextTop), maxTop),
        left: Math.min(Math.max(VIEWPORT_MARGIN, nextLeft), maxLeft),
      };
    });

    const endTooltipDrag = () => {
      tooltipDrag = null;
      window.removeEventListener("pointermove", onTooltipDragMove, true);
      window.removeEventListener("pointerup", endTooltipDrag, true);
      window.removeEventListener("pointercancel", endTooltipDrag, true);
    };

    const startTooltipDrag = guard("startTooltipDrag", (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const rect = tooltipRef.value?.getBoundingClientRect();
      tooltipDrag = {
        startX: event.clientX,
        startY: event.clientY,
        baseTop: manualTooltipPos.value?.top ?? rect?.top ?? 0,
        baseLeft: manualTooltipPos.value?.left ?? rect?.left ?? 0,
      };
      window.addEventListener("pointermove", onTooltipDragMove, true);
      window.addEventListener("pointerup", endTooltipDrag, true);
      window.addEventListener("pointercancel", endTooltipDrag, true);
    });

    const isTargetVisible = (selector: string) => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };

    const trainingContext = (): TrainingContext => ({
      route: route.path,
      // useAuth() exposes `user: Ref<AuthSession | null>` (AuthSession = User, see Auth.tsx).
      user: auth.user.value ? { role: auth.user.value.role } : null,
      dom: (selector: string) => isTargetVisible(selector),
    });

    const evaluate = (condition: Parameters<typeof evaluateCondition>[0]) =>
      evaluateCondition(condition, trainingContext());

    const stepValidationOk = (
      step: { validation?: Parameters<typeof evaluate>[0]; requireSelector?: string } | null | undefined,
    ) => {
      if (!step) return true;
      if (step.validation !== undefined) return evaluate(step.validation);
      if (step.requireSelector) return isTargetVisible(step.requireSelector);
      return true;
    };

    const { stop: stopTriggerController } = installTriggerController({
      routePath: computed(() => route.path),
      context: trainingContext,
      isActive: () => activeTutorial.value !== null,
      isCompleted,
      start: (id) => start(id),
    });

    // Bridge the active step's required overlay to the demo-data layer so the
    // Tables/POS overlay owners can reconcile (close strays, keep/open what the
    // step needs). Only meaningful for data-backed tutorials; null otherwise.
    const syncRequiredOverlay = () => {
      const tutorial = activeTutorial.value;
      const dataBacked = !!tutorial && isTutorialDataActive();
      const step = activeStep.value;
      let overlay = dataBacked ? (step?.overlay ?? null) : null;
      // Keep modals open on the final (end) card: if the last step declares no overlay,
      // retain whatever the previous step required, so reaching the summary card doesn't
      // tear down the open sheet/menu. It is closed only on actual exit (cleanupAfterExit
      // nulls the overlay and bumps the tick).
      if (
        dataBacked &&
        tutorial &&
        !step?.overlay &&
        activeStepIndex.value > 0 &&
        activeStepIndex.value === tutorial.steps.length - 1
      ) {
        overlay = requiredTutorialOverlay.value;
      }
      requiredTutorialOverlay.value = overlay;
      // Bump the tick so owners reconcile on every step, even when the overlay value is
      // unchanged across consecutive steps.
      tutorialStepTick.value += 1;
    };

    const maybeNavigateForStep = () => {
      const step = activeStep.value;
      const dest = step?.navigateTo;
      if (!dest) return;
      try {
        if (router.currentRoute.value.path !== dest) {
          router.push(dest);
        }
      } catch (e) {
        console.warn("Training navigateTo failed", dest, e);
      }
    };

    watch(activeStepIndex, () => {
      try {
        maybeNavigateForStep();
        syncRequiredOverlay();
        syncPosition();
      } catch (err) {
        console.warn("Training step change failed", err);
      }
    });
    watch(activeTutorial, (t) => {
      try {
        // Bridge to the demo-data layer: pages and the (non-Vue) api layer read this
        // flag to swap in fake tables/menu while a data-backed tutorial is active.
        activeTutorialDataId.value = t ? t.id : null;
        syncRequiredOverlay();
        if (t) {
          maybeNavigateForStep();
        }
      } catch (err) {
        console.warn("Training tutorial change failed", err);
      }
    });
    // Seed the demo-data locale now (the LocalizationProvider only mounts its
    // children once a locale is loaded, so this is already a real locale).
    if (localization.locale.value) tutorialLocale.value = localization.locale.value;
    watch(
      () => localization.locale.value,
      (loc) => {
        try {
          // Mirror the app locale into the demo-data layer so tutorial menu
          // items render in the current language (read at POS fetch time).
          if (loc) tutorialLocale.value = loc;
          refreshRect();
        } catch (err) {
          console.warn("Training locale change failed", err);
        }
      },
    );

    const consumeTutorialQuery = () => {
      try {
        const raw = route.query.tutorial;
        const id = Array.isArray(raw) ? raw[0] : raw;
        if (!id || typeof id !== "string") return;
        const tutorial = getTrainingTutorial(id);
        const { tutorial: _omit, ...rest } = route.query;
        router.replace({ path: route.path, query: rest, hash: route.hash });
        if (!tutorial) return;
        window.setTimeout(() => start(id, { force: true }), 300);
      } catch (err) {
        console.warn("Training consumeTutorialQuery failed", err);
      }
    };

    watch(
      () => route.query.tutorial,
      (val) => {
        if (val) consumeTutorialQuery();
      },
      { immediate: true },
    );

    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    window.addEventListener("mousemove", onUserActivity, true);
    window.addEventListener("touchstart", onUserActivity, true);

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
      window.removeEventListener("mousemove", onUserActivity, true);
      window.removeEventListener("touchstart", onUserActivity, true);
      endTooltipDrag();
      clearIdleHint();
      clearSyncTimers();
      stopTriggerController();
    });

    provide(TrainingContextKey, {
      activeTutorial,
      activeStepIndex,
      start,
      pause,
      skip,
      finish,
      isCompleted,
    });

    const renderRichTextBlock = (block: RichTextBlock, index: number) => {
      if (block.type === "paragraph") {
        return (
          <p key={index} class="mb-2 last:mb-0">
            {block.children.map(renderInline)}
          </p>
        );
      }

      if (block.type === "list") {
        return (
          <ul key={index} class="mb-2 list-disc space-y-1 pl-5 last:mb-0">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{item.map(renderInline)}</li>
            ))}
          </ul>
        );
      }

      return (
        <div key={index} class="mb-2 last:mb-0">
          <button
            type="button"
            class="group relative aspect-video w-full overflow-hidden rounded-lg bg-base-300 text-base-content"
            style={
              block.poster
                ? {
                    backgroundImage: `url(${block.poster})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : {}
            }
            aria-label={block.caption || "Play video"}
            onClick={() => (activeVideo.value = block)}
          >
            <span class="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/35" />
            <span class="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-base-100/90 text-primary shadow-xl transition-transform group-hover:scale-105">
              <span class="material-symbols-outlined text-4xl">play_arrow</span>
            </span>
          </button>
        </div>
      );
    };

    return () => {
      const tutorial = activeTutorial.value;
      const step = activeStep.value;
      if (!tutorial || !step) {
        return slots.default?.();
      }

      const vw = viewport.value.w;
      const vh = viewport.value.h;
      const rect = targetRect.value;
      const placement = pickPlacement(rect, step.placement, vw, vh, tooltipHeight.value);
      // The user can drag the card by its move handle; that manual position wins until
      // the next step re-anchors it to the new target.
      const pos = manualTooltipPos.value ?? computeTooltipPos(rect, placement, vw, vh, tooltipHeight.value);
      const isFirst = activeStepIndex.value === 0;
      const isLast = activeStepIndex.value === tutorial.steps.length - 1;
      const spotlight = rect
        ? {
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }
        : null;
      // The idle dot anchors on `hintTarget` when present (so it can focus a single
      // button inside a wider spotlight), otherwise on the spotlight centre.
      const hr = hintRect.value;
      const dotCenter =
        hr && spotlight
          ? { x: hr.left + hr.width / 2, y: hr.top + hr.height / 2 }
          : spotlight
            ? {
                x: spotlight.left + spotlight.width / 2,
                y: spotlight.top + spotlight.height / 2,
              }
            : null;
      const maskId = `training-mask-${tutorial.id.replace(/[^a-z0-9]/gi, "-")}`;
      const title = localizeText(step.title, locale.value);
      const tutorialName = localizeText(tutorial.title, locale.value);
      const body = localizeText(step.body, locale.value);
      const bodyBlocks = parseRichText(body);
      const stepLabel = copy.value.step
        .replace("{0}", String(activeStepIndex.value + 1))
        .replace("{1}", String(tutorial.steps.length));

      return (
        <>
          {slots.default?.()}
          {!activeVideo.value ? (
            <Teleport to="body">
              {/* Sit above the table-detail sheet / floor-plan forms (z-index: 9999),
                  so the spotlight + tooltip stay in front of the highlighted zone when a
                  step opens a sheet/modal (e.g. the payment steps). */}
              <div class="fixed inset-0 z-[10000] pointer-events-none">
                {spotlight ? (
                  // Spotlight step: the rest of the screen stays fully interactive while
                  // a step is highlighted. The dim is purely visual (the SVG layer below
                  // is pointer-events-none), so we render NO click-blockers here — the
                  // user can keep using the app, and the only way to dismiss the tutorial
                  // is the X (or controls) inside the tooltip.
                  null
                ) : (
                  // Center step (no spotlight): also fully click-through. The whole page
                  // stays interactive; only the tooltip captures clicks. The dim is purely
                  // visual and the tutorial is dismissed via the X (or controls) only.
                  null
                )}
                <svg width="100%" height="100%" class="absolute inset-0 pointer-events-none">
                  <defs>
                    <mask id={maskId}>
                      <rect width="100%" height="100%" fill="white" />
                      {spotlight ? (
                        <rect
                          x={spotlight.left}
                          y={spotlight.top}
                          width={spotlight.width}
                          height={spotlight.height}
                          rx="10"
                          ry="10"
                          fill="black"
                        />
                      ) : null}
                    </mask>
                  </defs>
                  <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask={`url(#${maskId})`} />
                  {spotlight ? (
                    <rect
                      x={spotlight.left}
                      y={spotlight.top}
                      width={spotlight.width}
                      height={spotlight.height}
                      rx="10"
                      ry="10"
                      fill="none"
                      stroke="hsl(var(--p))"
                      stroke-width="2"
                      class="drop-shadow"
                      pointer-events="none"
                    />
                  ) : null}
                </svg>

                {spotlight && showIdleHint.value ? (
                  step.hint === "swipe-horizontal" ? (
                    (() => {
                      const trackWidth = Math.min(viewport.value.w / 8, Math.max(40, spotlight.width - 24));
                      const trackLeft = spotlight.left + spotlight.width / 2 - trackWidth / 2;
                      return (
                        <div
                          class="absolute pointer-events-none overflow-hidden"
                          style={{
                            top: `${spotlight.top + spotlight.height / 2 - 8}px`,
                            left: `${trackLeft}px`,
                            width: `${trackWidth}px`,
                            height: "16px",
                          }}
                          aria-hidden="true"
                        >
                          <span
                            class="absolute top-1/2 -translate-y-1/2 inline-flex h-2.5 w-2.5 rounded-full shadow-[0_0_6px_rgba(239,68,68,0.55)]"
                            style={{
                              background: "rgba(239,68,68,0.85)",
                              animation: "training-slide 3.2s cubic-bezier(0.45,0,0.55,1) infinite",
                            }}
                          />
                        </div>
                      );
                    })()
                  ) : (
                    <div
                      class="absolute pointer-events-none flex items-center justify-center"
                      style={{
                        top: `${(dotCenter?.y ?? spotlight.top + spotlight.height / 2) - 16}px`,
                        left: `${(dotCenter?.x ?? spotlight.left + spotlight.width / 2) - 16}px`,
                        width: "32px",
                        height: "32px",
                      }}
                      aria-hidden="true"
                    >
                      <span
                        class="absolute inline-flex h-full w-full rounded-full opacity-75"
                        style={{
                          background: "rgb(239,68,68)",
                          animation: "training-ping 1.2s cubic-bezier(0,0,0.2,1) infinite",
                        }}
                      />
                      <span
                        class="relative inline-flex h-3 w-3 rounded-full"
                        style={{ background: "rgb(239,68,68)" }}
                      />
                    </div>
                  )
                ) : null}

                <style>{`
                @keyframes training-ping {
                  0% { transform: scale(1); opacity: 0.75; }
                  75%, 100% { transform: scale(2.4); opacity: 0; }
                }
                @keyframes training-slide {
                  0%   { left: 0%;                 opacity: 0; }
                  20%  { opacity: 0.85; }
                  50%  { left: calc(100% - 10px);  opacity: 0.85; }
                  80%  { opacity: 0.85; }
                  100% { left: 0%;                 opacity: 0; }
                }
                @keyframes training-pulse-btn {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
                  50% { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
                }
                .training-pulse-btn {
                  animation: training-pulse-btn 1.2s ease-out infinite;
                }
              `}</style>

                <div
                  ref={(el) => (tooltipRef.value = el as HTMLElement | null)}
                  class="absolute w-80 max-w-[calc(100vw-24px)] rounded-lg bg-base-100 p-5 shadow-2xl ring-1 ring-base-content/10 pointer-events-auto"
                  style={{
                    top: `${pos.top}px`,
                    left: `${pos.left}px`,
                  }}
                  onClick={(event: Event) => event.stopPropagation()}
                >
                  <div class="mb-1 flex items-center justify-between gap-2">
                    {editMode.value && editDrafts.value ? (
                      <input
                        class="input input-bordered input-sm flex-1 min-w-0 text-sm font-semibold"
                        value={editDrafts.value.tutorialTitle}
                        onInput={(event: Event) => {
                          if (editDrafts.value)
                            editDrafts.value.tutorialTitle = (event.target as HTMLInputElement).value;
                        }}
                      />
                    ) : (
                      <span class="text-sm font-semibold text-base-content truncate">{tutorialName}</span>
                    )}
                    <div class="flex items-center gap-1 shrink-0">
                      {isDev && !editMode.value ? (
                        <button
                          class="btn btn-ghost btn-xs btn-circle"
                          onClick={enterEditMode}
                          aria-label="Edit tutorial"
                          title="Edit (dev)"
                        >
                          <span class="material-symbols-outlined text-base">edit</span>
                        </button>
                      ) : null}
                      <button
                        class="btn btn-ghost btn-xs btn-circle cursor-move touch-none"
                        style="touch-action: none"
                        onPointerdown={startTooltipDrag}
                        aria-label="Move tutorial card"
                        title="Drag to move"
                      >
                        <span class="material-symbols-outlined text-base">open_with</span>
                      </button>
                      <button
                        class="btn btn-ghost btn-xs btn-circle"
                        onClick={editMode.value ? cancelEdit : skip}
                        aria-label={copy.value.skip}
                      >
                        <span class="material-symbols-outlined text-base">close</span>
                      </button>
                    </div>
                  </div>

                  <div class="mb-3 flex items-center justify-between gap-3">
                    <span class="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
                      {stepLabel}
                    </span>
                    <select
                      class="select select-ghost select-xs max-w-[7rem]"
                      value={locale.value}
                      onChange={(event: Event) => {
                        const next = (event.target as HTMLSelectElement).value;
                        if (next && next !== locale.value) {
                          localization.setLocale(next);
                        }
                      }}
                      onClick={(event: Event) => event.stopPropagation()}
                      aria-label="Language"
                    >
                      {supportedLanguages.map((lang) => (
                        <option key={lang.id} value={lang.id}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div class="mb-3 flex items-center gap-1">
                    {tutorial.steps.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        class={`h-2 flex-1 rounded-full transition-colors ${
                          index === activeStepIndex.value
                            ? "bg-primary"
                            : index < activeStepIndex.value
                              ? "bg-primary/45"
                              : "bg-base-content/15"
                        }`}
                        aria-label={`Step ${index + 1} — v${tutorial.version}`}
                        title={`Step ${index + 1} of ${tutorial.steps.length} · v${tutorial.version}`}
                        onClick={() => jumpToStep(index)}
                      />
                    ))}
                  </div>

                  {editMode.value && editDrafts.value ? (
                    <>
                      <input
                        class="input input-bordered input-sm mb-2 w-full text-base font-semibold"
                        value={editDrafts.value.stepTitle}
                        onInput={(event: Event) => {
                          if (editDrafts.value) editDrafts.value.stepTitle = (event.target as HTMLInputElement).value;
                        }}
                        placeholder="Step title"
                      />
                      <textarea
                        class="textarea textarea-bordered mb-2 w-full text-sm leading-relaxed font-mono"
                        rows={6}
                        value={editDrafts.value.stepBody}
                        onInput={(event: Event) => {
                          if (editDrafts.value) editDrafts.value.stepBody = (event.target as HTMLTextAreaElement).value;
                        }}
                        placeholder="Step body (markdown)"
                      />
                      <label class="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
                        Tutorial summary
                      </label>
                      <textarea
                        class="textarea textarea-bordered mb-3 w-full text-xs"
                        rows={2}
                        value={editDrafts.value.tutorialSummary}
                        onInput={(event: Event) => {
                          if (editDrafts.value)
                            editDrafts.value.tutorialSummary = (event.target as HTMLTextAreaElement).value;
                        }}
                        placeholder="Shown in the help dropdown"
                      />
                      <div class="mb-1 grid grid-cols-2 gap-2">
                        <label class="flex flex-col gap-1">
                          <span class="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
                            Who can see it
                          </span>
                          <select
                            class="select select-bordered select-xs"
                            value={editDrafts.value.visibility}
                            onChange={(event: Event) => {
                              if (editDrafts.value)
                                editDrafts.value.visibility = (event.target as HTMLSelectElement)
                                  .value as ArticleVisibility;
                            }}
                          >
                            {articleVisibilityOptions.map((option) => (
                              <option key={option} value={option}>
                                {roleVisibilityLabels[option]}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label class="flex flex-col gap-1">
                          <span class="text-[11px] font-semibold uppercase tracking-wide text-base-content/50">
                            Where it shows
                          </span>
                          <select
                            class="select select-bordered select-xs"
                            value={editDrafts.value.environmentVisibility}
                            onChange={(event: Event) => {
                              if (editDrafts.value)
                                editDrafts.value.environmentVisibility = (event.target as HTMLSelectElement)
                                  .value as EnvironmentVisibility;
                            }}
                          >
                            {environmentVisibilityOptions.map((option) => (
                              <option key={option} value={option}>
                                {environmentVisibilityLabels[option]}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 class="mb-1 text-lg font-semibold text-base-content">{title}</h3>
                      <div class="mb-4 text-sm leading-relaxed text-base-content/70">
                        {bodyBlocks.map(renderRichTextBlock)}
                      </div>
                    </>
                  )}

                  {editMode.value ? (
                    <div class="flex flex-col gap-2">
                      <label class="flex items-center gap-2 text-xs text-base-content/70 cursor-pointer">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-xs"
                          checked={autoTranslate.value}
                          onChange={(event: Event) => {
                            autoTranslate.value = (event.target as HTMLInputElement).checked;
                          }}
                        />
                        Auto-translate to other languages on save
                      </label>

                      <div class="rounded-md border border-base-300">
                        <button
                          type="button"
                          class="flex w-full items-center justify-between px-2 py-1.5 text-xs font-semibold text-base-content/70"
                          onClick={() => {
                            showHistory.value = !showHistory.value;
                            if (showHistory.value) void loadHistory();
                          }}
                        >
                          <span class="flex items-center gap-1">
                            <span class="material-symbols-outlined text-sm">history</span>
                            Version history
                            <span class="font-normal text-base-content/40">· v{tutorial.version}</span>
                          </span>
                          <span class="material-symbols-outlined text-sm">
                            {showHistory.value ? "expand_less" : "expand_more"}
                          </span>
                        </button>
                        {showHistory.value ? (
                          <div class="max-h-44 overflow-y-auto border-t border-base-300 px-2 py-1.5">
                            {historyLoading.value ? (
                              <div class="py-2 text-center text-xs text-base-content/40">Loading…</div>
                            ) : historyError.value ? (
                              <div class="py-1 text-xs text-error">{historyError.value}</div>
                            ) : historyEntries.value.length === 0 ? (
                              <div class="py-2 text-center text-xs text-base-content/40">
                                No previous versions yet.
                              </div>
                            ) : (
                              <ul class="flex flex-col gap-1">
                                {historyEntries.value.map((entry) => (
                                  <li
                                    key={entry.index}
                                    class="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs hover:bg-base-200"
                                  >
                                    <span class="min-w-0">
                                      <span class="font-mono font-semibold">v{entry.version}</span>
                                      <span class="ml-2 text-base-content/50">
                                        {new Date(entry.savedAt).toLocaleString()}
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      class="btn btn-ghost btn-xs shrink-0"
                                      disabled={restoring.value !== null}
                                      onClick={() => restoreVersion(entry.index)}
                                    >
                                      {restoring.value === entry.index ? "Restoring…" : "Restore"}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ) : null}
                      </div>

                      {saveError.value ? <div class="text-xs text-error">{saveError.value}</div> : null}
                      <div class="flex items-center justify-end gap-2">
                        <button class="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving.value}>
                          Cancel
                        </button>
                        <button class="btn btn-primary btn-sm" onClick={saveEdits} disabled={saving.value}>
                          {saving.value ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div class="flex items-center justify-between gap-2">
                      <div>
                        {!isFirst ? (
                          <button class="btn btn-sm" onClick={goBack}>
                            <span class="material-symbols-outlined text-base">arrow_back</span>
                            {copy.value.back}
                          </button>
                        ) : null}
                      </div>
                      <div class="flex items-center gap-2">
                        {/* Next-only progression: the Next button always renders and is the
                            sole forward control. It routes through goNext({ manual: true }),
                            which honours any step validation before advancing. */}
                        {isLast && tutorial.next ? (
                          <>
                            <button class="btn btn-sm" onClick={finish}>
                              {copy.value.finish}
                            </button>
                            <button
                              class={`btn btn-primary btn-sm gap-1 ${showIdleHint.value ? "training-pulse-btn" : ""}`}
                              onClick={startNext}
                            >
                              {copy.value.next}
                              <span class="material-symbols-outlined text-base">arrow_forward</span>
                            </button>
                          </>
                        ) : (
                          <button
                            class={`btn btn-primary btn-sm gap-1 ${showIdleHint.value ? "training-pulse-btn" : ""}`}
                            onClick={() => goNext({ manual: true })}
                          >
                            {isLast ? copy.value.finish : copy.value.next}
                            <span class="material-symbols-outlined text-base">
                              {isLast ? "check" : "arrow_forward"}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Teleport>
          ) : null}
          {activeVideo.value ? (
            <LemmonModal
              title={activeVideo.value.caption || "Video"}
              onClose={() => (activeVideo.value = null)}
              disableBottomSheet={true}
            >
              <div class="p-4">
                <video
                  class="aspect-video w-full rounded bg-black"
                  src={activeVideo.value.src}
                  poster={activeVideo.value.poster}
                  controls
                  autoplay
                />
              </div>
            </LemmonModal>
          ) : null}
        </>
      );
    };
  },
});

export function useTraining(): TrainingContextType {
  const context = inject<TrainingContextType>(TrainingContextKey);
  if (!context) {
    throw new Error("useTraining must be used within a TrainingProvider");
  }
  return context;
}
